import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Package, Search, Tag, RefreshCw, Plus, Edit, Trash2, 
  Loader2, ChevronLeft, ChevronRight, Filter, Palette, Download, AlertTriangle, CheckSquare,
  Upload, Ban
} from 'lucide-react';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel
} from '../components/ui/alert-dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ItemMasters = () => {
  const { getAuthHeaders, isAdmin } = useAuth();
  
  // Data state
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState({ total_items: 0, categorized: 0, uncategorized: 0 });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  
  // Filter state
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showUncategorized, setShowUncategorized] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 50;
  
  // Category management
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '', color: '#6B7280' });
  const [savingCategory, setSavingCategory] = useState(false);

  // Delete uncategorized
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Selection state for selective deletion
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [showSelectedDeleteDialog, setShowSelectedDeleteDialog] = useState(false);

  // Derive whether checkboxes should show
  const showCheckboxes = isAdmin && showUncategorized;

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString()
      });
      
      if (search) params.append('search', search);
      if (filterCategory && filterCategory !== 'all') params.append('category_id', filterCategory);
      if (showUncategorized) params.append('uncategorized_only', 'true');
      
      const response = await axios.get(`${API}/item-masters?${params}`, {
        headers: getAuthHeaders()
      });
      
      setItems(response.data.items);
      setCategories(response.data.categories);
      setSummary(response.data.summary);
      setTotal(response.data.total);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      toast.error('Failed to load items');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, page, search, filterCategory, showUncategorized]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Clear selection when page/filter changes
  useEffect(() => {
    setSelectedItems(new Set());
  }, [page, search, filterCategory, showUncategorized]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/item-masters/sync`, {}, {
        headers: getAuthHeaders()
      });
      toast.success(`Sync complete: ${response.data.added} added, ${response.data.updated} updated`);
      fetchItems();
    } catch (error) {
      toast.error('Failed to sync items');
    } finally {
      setSyncing(false);
    }
  };

  const handleDownloadUncategorized = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/item-masters/uncategorized-export`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'uncategorized_items.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.status === 404 ? 'No uncategorized items found' : 'Failed to download');
    } finally {
      setDownloading(false);
    }
  };

  const handleDeleteUncategorized = async () => {
    setDeleting(true);
    try {
      const response = await axios.delete(`${API}/item-masters/uncategorized`, {
        headers: getAuthHeaders()
      });
      toast.success(`Deleted ${response.data.deleted_items} uncategorized items`);
      setShowDeleteDialog(false);
      setSelectedItems(new Set());
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete items');
    } finally {
      setDeleting(false);
    }
  };

  // --- Selective deletion helpers ---
  const toggleItem = (itemName) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemName)) next.delete(itemName);
      else next.add(itemName);
      return next;
    });
  };

  const togglePageAll = () => {
    const uncatOnPage = items.filter(i => !i.category_id).map(i => i.item_name);
    const allSelected = uncatOnPage.every(n => selectedItems.has(n));
    setSelectedItems(prev => {
      const next = new Set(prev);
      uncatOnPage.forEach(n => allSelected ? next.delete(n) : next.add(n));
      return next;
    });
  };

  const pageAllChecked = items.length > 0 && items.filter(i => !i.category_id).every(i => selectedItems.has(i.item_name));

  const handleDeleteSelected = async () => {
    if (selectedItems.size === 0) return;
    setDeleting(true);
    try {
      const response = await axios.post(
        `${API}/item-masters/delete-selected`,
        { item_names: Array.from(selectedItems) },
        { headers: getAuthHeaders() }
      );
      toast.success(`Deleted ${response.data.deleted_items} items`);
      setShowSelectedDeleteDialog(false);
      setSelectedItems(new Set());
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete items');
    } finally {
      setDeleting(false);
    }
  };

  const handleCategoryChange = async (itemName, categoryId) => {
    try {
      await axios.put(
        `${API}/item-masters/${encodeURIComponent(itemName)}/category`,
        { category_id: categoryId || null },
        { headers: getAuthHeaders() }
      );
      toast.success('Category updated');
      
      // Update local state
      setItems(prev => prev.map(item => 
        item.item_name === itemName 
          ? { ...item, category_id: categoryId, category: categories.find(c => c.id === categoryId) || null }
          : item
      ));
      
      // Update summary
      setSummary(prev => {
        const wasUncategorized = items.find(i => i.item_name === itemName)?.category_id === null;
        const isNowUncategorized = !categoryId;
        
        if (wasUncategorized && !isNowUncategorized) {
          return { ...prev, categorized: prev.categorized + 1, uncategorized: prev.uncategorized - 1 };
        } else if (!wasUncategorized && isNowUncategorized) {
          return { ...prev, categorized: prev.categorized - 1, uncategorized: prev.uncategorized + 1 };
        }
        return prev;
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update category');
    }
  };

  // --- Non-running item handlers ---
  const handleToggleNonRunning = async (itemName, currentValue) => {
    try {
      await axios.put(
        `${API}/item-masters/${encodeURIComponent(itemName)}/non-running`,
        { non_running: !currentValue },
        { headers: getAuthHeaders() }
      );
      setItems(prev => prev.map(item =>
        item.item_name === itemName ? { ...item, non_running: !currentValue } : item
      ));
      toast.success(`${itemName} marked as ${!currentValue ? 'non-running' : 'running'}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDownloadNRTemplate = async () => {
    try {
      const response = await axios.get(`${API}/item-masters/non-running/template`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'non_running_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleUploadNRFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API}/item-masters/non-running/bulk-upload`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} items not found`);
      }
      fetchItems();
    } catch (error) {
      toast.error('Failed to upload file');
    }
    e.target.value = '';
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error('Category name is required');
      return;
    }
    
    setSavingCategory(true);
    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, categoryForm, {
          headers: getAuthHeaders()
        });
        toast.success('Category updated');
      } else {
        await axios.post(`${API}/categories`, categoryForm, {
          headers: getAuthHeaders()
        });
        toast.success('Category created');
      }
      
      setShowCategoryDialog(false);
      setCategoryForm({ name: '', description: '', color: '#6B7280' });
      setEditingCategory(null);
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    } finally {
      setSavingCategory(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Delete this category? Items will become uncategorized.')) return;
    
    try {
      await axios.delete(`${API}/categories/${categoryId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Category deleted');
      fetchItems();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const openEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      description: category.description || '',
      color: category.color || '#6B7280'
    });
    setShowCategoryDialog(true);
  };

  const openNewCategory = () => {
    setEditingCategory(null);
    setCategoryForm({ name: '', description: '', color: '#6B7280' });
    setShowCategoryDialog(true);
  };

  const colorOptions = [
    { value: '#3B82F6', label: 'Blue' },
    { value: '#10B981', label: 'Green' },
    { value: '#F59E0B', label: 'Amber' },
    { value: '#EF4444', label: 'Red' },
    { value: '#8B5CF6', label: 'Purple' },
    { value: '#EC4899', label: 'Pink' },
    { value: '#6B7280', label: 'Gray' },
    { value: '#14B8A6', label: 'Teal' }
  ];

  return (
    <div className="space-y-6" data-testid="item-masters-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Item Masters</h1>
          <p className="text-slate-500">Manage product catalog and categories</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <>
              <Button
                variant="outline"
                onClick={handleSync}
                disabled={syncing}
                data-testid="sync-items-btn"
              >
                {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Sync Items
              </Button>
              {summary.uncategorized > 0 && (
                <Button
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                  onClick={() => setShowDeleteDialog(true)}
                  data-testid="delete-uncategorized-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Uncategorized
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Total Items</p>
                <p className="text-2xl font-bold text-blue-800">{summary.total_items.toLocaleString()}</p>
              </div>
              <Package className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Categorized</p>
                <p className="text-2xl font-bold text-green-800">{summary.categorized.toLocaleString()}</p>
              </div>
              <Tag className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600 font-medium">Uncategorized</p>
                <p className="text-2xl font-bold text-orange-800">{summary.uncategorized.toLocaleString()}</p>
              </div>
              <Filter className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 font-medium">Non-Running</p>
                <p className="text-2xl font-bold text-slate-800">{(summary.non_running || 0).toLocaleString()}</p>
              </div>
              <Ban className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Non-Running Template Actions */}
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={handleDownloadNRTemplate} data-testid="download-nr-template-btn">
          <Download className="w-3.5 h-3.5 mr-1.5" />
          Non-Running Template
        </Button>
        <label>
          <input type="file" accept=".xlsx,.xls" onChange={handleUploadNRFile} className="hidden" data-testid="upload-nr-file" />
          <Button variant="outline" size="sm" asChild>
            <span>
              <Upload className="w-3.5 h-3.5 mr-1.5" />
              Upload Non-Running
            </span>
          </Button>
        </label>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="items" className="space-y-4">
        <TabsList>
          <TabsTrigger value="items" data-testid="items-tab">Items ({total})</TabsTrigger>
          {isAdmin && <TabsTrigger value="categories" data-testid="categories-tab">Categories ({categories.length})</TabsTrigger>}
        </TabsList>

        {/* Items Tab */}
        <TabsContent value="items">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Search */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search items..."
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    className="pl-10"
                    data-testid="search-items"
                  />
                </div>
                
                {/* Category Filter */}
                <Select value={filterCategory} onValueChange={(v) => { setFilterCategory(v); setPage(1); setShowUncategorized(false); }}>
                  <SelectTrigger className="w-48" data-testid="filter-category">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                          {cat.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Uncategorized Toggle */}
                <Button
                  variant={showUncategorized ? 'default' : 'outline'}
                  onClick={() => { setShowUncategorized(!showUncategorized); setFilterCategory('all'); setPage(1); }}
                  data-testid="uncategorized-filter"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Uncategorized Only
                </Button>
              </div>
            </CardHeader>
            
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1,2,3,4,5].map(i => (
                    <Skeleton key={i} className="h-14 w-full" />
                  ))}
                </div>
              ) : items.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Package className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No items found</p>
                  {isAdmin && (
                    <Button variant="outline" className="mt-4" onClick={handleSync}>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Sync from Stock/Sales
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 text-left">
                        <tr>
                          {showCheckboxes && (
                            <th className="px-3 py-3 w-10">
                              <Checkbox
                                checked={pageAllChecked}
                                onCheckedChange={togglePageAll}
                                data-testid="select-all-checkbox"
                              />
                            </th>
                          )}
                          <th className="px-4 py-3 text-sm font-medium text-slate-600">SKU</th>
                          <th className="px-4 py-3 text-sm font-medium text-slate-600">Item Name</th>
                          <th className="px-4 py-3 text-sm font-medium text-slate-600">Category</th>
                          <th className="px-4 py-3 text-sm font-medium text-slate-600 text-center">Non-Running</th>
                          <th className="px-4 py-3 text-sm font-medium text-slate-600 text-center">Depots</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {items.map((item, idx) => (
                          <tr
                            key={item.item_name}
                            className={`hover:bg-slate-50 ${showCheckboxes && selectedItems.has(item.item_name) ? 'bg-red-50' : ''}`}
                          >
                            {showCheckboxes && (
                              <td className="px-3 py-3">
                                <Checkbox
                                  checked={selectedItems.has(item.item_name)}
                                  onCheckedChange={() => toggleItem(item.item_name)}
                                  data-testid={`select-item-${idx}`}
                                />
                              </td>
                            )}
                            <td className="px-4 py-3 text-sm font-mono text-slate-600">{item.sku || '-'}</td>
                            <td className="px-4 py-3 text-sm text-slate-800 font-medium">{item.item_name}</td>
                            <td className="px-4 py-3">
                              <Select
                                value={item.category_id || 'none'}
                                onValueChange={(v) => handleCategoryChange(item.item_name, v === 'none' ? null : v)}
                              >
                                <SelectTrigger className="w-44 h-8" data-testid={`category-select-${idx}`}>
                                  <SelectValue>
                                    {item.category ? (
                                      <div className="flex items-center gap-2">
                                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.category.color }} />
                                        <span className="text-sm">{item.category.name}</span>
                                      </div>
                                    ) : (
                                      <span className="text-slate-400 text-sm">Select category</span>
                                    )}
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">
                                    <span className="text-slate-400">No category</span>
                                  </SelectItem>
                                  {categories.map(cat => (
                                    <SelectItem key={cat.id} value={cat.id}>
                                      <div className="flex items-center gap-2">
                                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cat.color }} />
                                        {cat.name}
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <Switch
                                checked={!!item.non_running}
                                onCheckedChange={() => handleToggleNonRunning(item.item_name, !!item.non_running)}
                                data-testid={`non-running-toggle-${idx}`}
                              />
                            </td>
                            <td className="px-4 py-3 text-center">
                              <Badge variant="secondary">{item.total_depots}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Selection Action Bar */}
                  {showCheckboxes && selectedItems.size > 0 && (
                    <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg px-4 py-3 mt-4" data-testid="selection-bar">
                      <div className="flex items-center gap-2">
                        <CheckSquare className="w-4 h-4 text-red-600" />
                        <span className="text-sm font-medium text-red-700">
                          {selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''} selected
                        </span>
                        <button
                          onClick={() => setSelectedItems(new Set())}
                          className="text-xs text-red-500 hover:text-red-700 underline ml-1"
                          data-testid="clear-selection-btn"
                        >
                          Clear
                        </button>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-red-300 text-red-600 hover:bg-red-100"
                          onClick={() => setShowSelectedDeleteDialog(true)}
                          data-testid="delete-selected-btn"
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                          Delete Selected
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <p className="text-sm text-slate-500">
                      Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, total)} of {total} items
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm text-slate-600">
                        Page {page} of {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Categories Tab (Admin Only) */}
        {isAdmin && (
          <TabsContent value="categories">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Manage Categories</CardTitle>
                <Button onClick={openNewCategory} data-testid="new-category-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Category
                </Button>
              </CardHeader>
              <CardContent>
                {categories.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Tag className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No categories yet</p>
                    <Button variant="outline" className="mt-4" onClick={openNewCategory}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create First Category
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {categories.map(cat => (
                      <Card key={cat.id} className="border-l-4" style={{ borderLeftColor: cat.color }}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <div className="w-4 h-4 rounded-full" style={{ backgroundColor: cat.color }} />
                                <h3 className="font-semibold text-slate-800">{cat.name}</h3>
                              </div>
                              <p className="text-sm text-slate-500">{cat.description || 'No description'}</p>
                            </div>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openEditCategory(cat)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteCategory(cat.id)} className="text-red-500 hover:text-red-700">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Edit Category' : 'New Category'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g., Lighting"
                data-testid="category-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={categoryForm.description}
                onChange={(e) => setCategoryForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Optional description"
              />
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2">
                {colorOptions.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setCategoryForm(f => ({ ...f, color: opt.value }))}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      categoryForm.color === opt.value ? 'border-slate-800 scale-110' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: opt.value }}
                    title={opt.label}
                  />
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
              <Palette className="w-5 h-5 text-slate-400" />
              <span className="text-sm text-slate-600">Preview:</span>
              <Badge style={{ backgroundColor: categoryForm.color, color: 'white' }}>
                {categoryForm.name || 'Category Name'}
              </Badge>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveCategory} disabled={savingCategory} data-testid="save-category-btn">
              {savingCategory ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingCategory ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Uncategorized Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete Uncategorized Items
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 pt-2">
                <p className="text-sm text-slate-600">
                  <span className="font-semibold text-red-600">{summary.uncategorized.toLocaleString()}</span> uncategorized items will be permanently deleted from all collections (stock, sales, buffer, orders, production, etc.).
                </p>
                <p className="text-sm text-slate-500">
                  This action cannot be undone. Download the list first to keep a backup.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadUncategorized}
                  disabled={downloading}
                  data-testid="download-uncategorized-btn"
                >
                  {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Download List (Excel)
                </Button>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={handleDeleteUncategorized}
              disabled={deleting}
              data-testid="confirm-delete-uncategorized-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete All
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Selected Items Confirmation Dialog */}
      <AlertDialog open={showSelectedDeleteDialog} onOpenChange={setShowSelectedDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete Selected Items
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 pt-2">
                <p className="text-sm text-slate-600">
                  <span className="font-semibold text-red-600">{selectedItems.size.toLocaleString()}</span> selected item{selectedItems.size !== 1 ? 's' : ''} will be permanently deleted from all collections (stock, sales, buffer, orders, production, etc.).
                </p>
                <p className="text-sm text-slate-500">
                  This action cannot be undone.
                </p>
                <div className="max-h-40 overflow-y-auto border rounded-md p-2 bg-slate-50">
                  {Array.from(selectedItems).sort().map(name => (
                    <p key={name} className="text-xs text-slate-600 py-0.5 truncate">{name}</p>
                  ))}
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-selected-delete-btn">Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={handleDeleteSelected}
              disabled={deleting}
              data-testid="confirm-delete-selected-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete {selectedItems.size} Item{selectedItems.size !== 1 ? 's' : ''}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ItemMasters;
